import React, { useEffect, useState } from "react";

import * as Sentry from "@sentry/react";
import { compact, find } from "lodash/fp";
import { useSearchParams } from "react-router-dom";

import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";

import useConnectCalendar from "@app/analytics/hooks/calendar/useConnectCalendar";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import useConnectedProviders from "@app/analytics/hooks/sso/useConnectedProviders";
import { CALENDAR_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import { toast } from "@app/common/hooks/useToasts";
import type { CalendarProviderId } from "@app/common/types/providerTypes";
import { isResponseErrorWithStatus } from "@app/common/utils";

import Checkbox from "@rui/Checkbox";
import ConfirmationModal from "@rui/ConfirmationModal";
import InfoPanel from "@rui/InfoPanel";
import { Bold } from "@rui/styles";

import ReportingSettingAccordion from "../ReportingSettingAccordion";
import { AccordionDetailsContent } from "../styles";

const REAUTH_CALENDAR_ACTION = "grant_calendar_edit_permission";
const TOOLTIP = {
  noParticipantAccess:
    "Change your default participant access above to update this setting",
  noCalendarConnected: "Connect a calendar provider to update this setting",
};

const ReauthAlert = ({
  onClick,
  providerId,
}: {
  onClick: (providerId: CalendarProviderId) => void;
  providerId: CalendarProviderId;
}) => (
  <InfoPanel
    severity="error"
    action={{ label: "Reauthorize", onClick: () => onClick(providerId) }}
  >
    {CALENDAR_PROVIDERS_BY_ID[providerId].displayName} requires reauthorization.
  </InfoPanel>
);

const UpdateCalendarEventSettings = () => {
  const [
    calendarProviderRequiringAuthorization,
    setCalendarProviderRequiringAuthorization,
  ] = useState<CalendarProviderId | null>(null);

  const [queryParams] = useSearchParams();
  const { userSettings, updateUserSettings } = useUserSettings();
  const { connectCalendar } = useConnectCalendar();

  const {
    connectedCalendarProviders,
    hasConnectedCalendarProviders,
    isLoading,
  } = useConnectedProviders();

  const reauthedCalendar = queryParams.get("action") === REAUTH_CALENDAR_ACTION;

  useEffect(() => {
    if (!reauthedCalendar) return;

    if (queryParams.get("success") === "true") {
      updateUserSettings.mutate({
        afterMeetingUpdateCalendarEventWithRecap: true,
      });
    } else {
      toast.error(
        "Something went wrong obtaining edit permissions to your calendar. Please try again later or contact support@read.ai if the problem persists."
      );
    }
  }, [reauthedCalendar]);

  const handleUpdateError = async (error: unknown) => {
    if (isResponseErrorWithStatus(error, 403)) {
      const json = await error.response.json();
      setCalendarProviderRequiringAuthorization(json.detail.calendar_provider);
      return;
    }
    Sentry.captureException(error);
  };

  const handleToggleUpdateCalendarEvent = (enable: boolean) => {
    updateUserSettings.mutate(
      { afterMeetingUpdateCalendarEventWithRecap: enable },
      { onError: handleUpdateError }
    );
  };

  const handleToggleNotifyInvitees = (
    event: React.SyntheticEvent,
    enable: boolean
  ) => {
    updateUserSettings.mutate(
      { afterCalendarEventRecapUpdateNotifyInvitees: enable },
      { onError: handleUpdateError }
    );
  };

  const handleReauthorizeCalendar = (providerId: CalendarProviderId) => {
    return connectCalendar(providerId, {
      action: REAUTH_CALENDAR_ACTION,
      returnTo: window.location.pathname,
    });
  };

  const noParticipantAccess =
    userSettings.internalParticipantAccessLevel === "no_access" &&
    userSettings.externalParticipantAccessLevel === "no_access";
  const noConnectedCalendars = !isLoading && !hasConnectedCalendarProviders;

  const connectedGoogleCalendar = find(
    { providerId: "google_calendar" },
    connectedCalendarProviders
  );
  const connectedMicrosoftCalendar = find(
    { providerId: "microsoft_graph_calendar" },
    connectedCalendarProviders
  );

  const showGoogleAuthWarning =
    userSettings.afterMeetingUpdateCalendarEventWithRecap &&
    Boolean(connectedGoogleCalendar) &&
    !connectedGoogleCalendar?.canEditCalendar;

  const showMicrosoftAuthWarning =
    userSettings.afterMeetingUpdateCalendarEventWithRecap &&
    Boolean(connectedMicrosoftCalendar) &&
    !connectedMicrosoftCalendar?.canEditCalendar;

  const isDisabled = noParticipantAccess || noConnectedCalendars;

  return (
    <>
      <ReportingSettingAccordion
        title="Update Calendar Event"
        subtitle="After a meeting, update the calendar event description with a summary and report link"
        onToggleEnabled={handleToggleUpdateCalendarEvent}
        enabled={
          userSettings.afterMeetingUpdateCalendarEventWithRecap && !isDisabled
        }
        opts={{
          disabled: isDisabled,
          disabledTooltip: noParticipantAccess
            ? TOOLTIP.noParticipantAccess
            : TOOLTIP.noCalendarConnected,
        }}
        alerts={compact([
          showGoogleAuthWarning && (
            <ReauthAlert
              onClick={handleReauthorizeCalendar}
              providerId="google_calendar"
            />
          ),
          showMicrosoftAuthWarning && (
            <ReauthAlert
              onClick={handleReauthorizeCalendar}
              providerId="microsoft_graph_calendar"
            />
          ),
        ])}
      >
        <AccordionDetailsContent>
          <FormControlLabel
            control={<Checkbox variant="filled" />}
            checked={userSettings.afterCalendarEventRecapUpdateNotifyInvitees}
            label={
              <Typography variant="bodySecondaryMed" color="shades.neutral.600">
                When the calendar event is updated, send update emails to all
                guests
              </Typography>
            }
            onChange={handleToggleNotifyInvitees}
          />
        </AccordionDetailsContent>
      </ReportingSettingAccordion>

      {calendarProviderRequiringAuthorization != null && (
        <ConfirmationModal
          open
          onClose={() => setCalendarProviderRequiringAuthorization(null)}
          onConfirm={() =>
            handleReauthorizeCalendar(calendarProviderRequiringAuthorization)
          }
          title="Heads up!"
          confirmProps={{ label: "Yes, continue" }}
          cancelProps={{ label: "Not now" }}
        >
          <Typography>
            To change this setting, Read needs to be able to edit your{" "}
            <Bold>
              {
                CALENDAR_PROVIDERS_BY_ID[calendarProviderRequiringAuthorization]
                  .displayName
              }
            </Bold>{" "}
            calendar. Do you want to grant edit permission?
          </Typography>
        </ConfirmationModal>
      )}
    </>
  );
};

export default UpdateCalendarEventSettings;
